import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { LuBookOpen, LuLightbulb, LuSparkles, LuVideo } from 'react-icons/lu';
import { Card } from '../../../design-system/components';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

const icons = [LuBookOpen, LuLightbulb, LuVideo, LuSparkles];

const fallbackStats = [
  { value: '100+', label: 'Lessons & Articles' },
  { value: '500+', label: 'Practice Scenarios' },
  { value: 'Live', label: 'Video Empathy' },
  { value: '24/7', label: 'AI Coaching' },
];

interface StatsResponse {
  content_count: string;
  practice_questions: string;
  ai_support: string;
  empathy_format: string;
}

export default function SocialProofBar() {
  const [stats, setStats] = useState(fallbackStats);

  useEffect(() => {
    const controller = new AbortController();

    fetch(`${API_URL}/public/stats`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch stats');
        return res.json() as Promise<StatsResponse>;
      })
      .then((data) => {
        setStats([
          { value: data.content_count, label: 'Lessons & Articles' },
          { value: data.practice_questions, label: 'Practice Scenarios' },
          { value: data.empathy_format, label: 'Video Empathy' },
          { value: data.ai_support, label: 'AI Coaching' },
        ]);
      })
      .catch(() => {
        // Keep fallback stats on error
      });

    return () => controller.abort();
  }, []);

  // One Card, four stats (redesign PR10): two by two on a phone, four
  // across from 768, where the card overlaps the foot of the hero.
  return (
    <section className="ee-landing-proof">
      <div className="ee-landing-container">
        <Card padding="none">
          <motion.div
            className="ee-landing-proof__grid"
            aria-live="polite"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {stats.map((stat, index) => {
              const Icon = icons[index];
              return (
                <motion.div
                  key={stat.label}
                  className="ee-landing-proof__stat"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Icon className="ee-landing-proof__icon" aria-hidden="true" focusable="false" />
                  <div className="ee-landing-proof__value">{stat.value}</div>
                  <div className="ee-landing-proof__label">{stat.label}</div>
                </motion.div>
              );
            })}
          </motion.div>
        </Card>
      </div>
    </section>
  );
}
